import React, { useEffect, useState } from 'react';
import { Card, Button, Row, Col, Dropdown, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  getAllQuestionGroups,
  removeQuestionGroup
} from 'services/questionGroups';
import { remove } from 'services/questions';
import AddQuestionGroupModal from './AddQuestionGroupModal';
import EditQuestionGroupModal from './EditQuestionGroupModal';
import AddQuestionModal from './AddQuestionModal';
import EditQuestionModal from './EditQuestionModal'; // Importa el modal de edición de preguntas
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import CardDropdown from 'components/common/CardDropdown';
import PropTypes from 'prop-types';

const EditQuestionsGroups = ({ survey_id }) => {
  const [questionGroups, setQuestionGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showEditQuestionModal, setShowEditQuestionModal] = useState(false); // Estado para el modal de edición de preguntas
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null); // Estado para el ID de la pregunta seleccionada
  const [reload, setReload] = useState(false);

  // Función para cargar los grupos de preguntas
  const fetchQuestionGroups = () => {
    setLoading(true);
    getAllQuestionGroups(survey_id)
      .then(response => {
        if (response.result) {
          setQuestionGroups(
            Array.isArray(response.questionGroups)
              ? response.questionGroups
              : []
          );
        } else {
          toast.error('Error al obtener los grupos de preguntas.', {
            theme: 'colored'
          });
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error al obtener los grupos de preguntas:', error);
        toast.error('Error al cargar los grupos de preguntas.', {
          theme: 'colored'
        });
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!survey_id) {
      toast.error('No se encontró el ID de la encuesta.', { theme: 'colored' });
      return;
    }
    fetchQuestionGroups();
  }, [survey_id, reload]);

  // Función para eliminar un grupo de preguntas
  const handleRemoveGroup = id => {
    removeQuestionGroup(id)
      .then(response => {
        if (response.result) {
          toast.success('Grupo de preguntas eliminado exitosamente.', {
            theme: 'colored'
          });
          setReload(!reload);
        } else {
          toast.error('Error al eliminar el grupo de preguntas.', {
            theme: 'colored'
          });
        }
      })
      .catch(error => {
        console.error('Error al eliminar el grupo de preguntas:', error);
        toast.error('Error al eliminar el grupo de preguntas.', {
          theme: 'colored'
        });
      });
  };

  return (
    <Card>
      <Card.Header>
        <Row className="g-3 align-items-center">
          <Col xs={12} md={6} lg={4}>
            <h5 className="mb-0 text-center text-md-start">
              Grupos de Preguntas
            </h5>
          </Col>
          <Col xs={12} md="auto" className="text-center">
            <Button
              variant="success"
              size="sm"
              onClick={() => setShowAddModal(true)}
            >
              Agregar Grupo
            </Button>
          </Col>
          <Col xs={12} md={6} lg={4} className="text-center text-md-end">
            <AdvanceTableSearchBox />
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="p-0">
        {loading ? (
          <div className="text-center py-3">
            Cargando grupos de preguntas...
          </div>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr className="table-secondary">
                <th>Título</th>
                <th>Mostrar Título</th>
                <th>Descripción</th>
                <th>Mostrar Desc.</th>
                <th>Orden</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {questionGroups.map(group => (
                <React.Fragment key={group.id}>
                  {/* Fila principal (Grupo de preguntas) */}
                  <tr className="table-primary">
                    <td>{group.title}</td>
                    <td className="text-center">
                      {group.show_title ? (
                        <span className="badge bg-success">Sí</span>
                      ) : (
                        <span className="badge bg-secondary">No</span>
                      )}
                    </td>
                    <td>{group.description}</td>
                    <td className="text-center">
                      {group.show_description ? (
                        <span className="badge bg-success">Sí</span>
                      ) : (
                        <span className="badge bg-secondary">No</span>
                      )}
                    </td>
                    <td>{group.order_index}</td>
                    <td>
                      <CardDropdown>
                        <div className="py-2">
                          <Dropdown.Item
                            onClick={() => {
                              setSelectedGroupId(group.id);
                              setShowEditModal(true);
                            }}
                          >
                            Editar
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSelectedGroupId(group.id);
                              setShowAddQuestionModal(true);
                            }}
                          >
                            Agregar Pregunta
                          </Dropdown.Item>
                          <Dropdown.Item
                            className="text-danger"
                            onClick={() => handleRemoveGroup(group.id)}
                          >
                            Eliminar
                          </Dropdown.Item>
                        </div>
                      </CardDropdown>
                    </td>
                  </tr>
                  {/* Subfilas (Preguntas y opciones) */}
                  {group.group_questions.map(question => (
                    <tr key={question.id}>
                      <td colSpan="6">
                        <Table striped bordered size="sm">
                          <thead>
                            <tr>
                              <th>Pregunta</th>
                              <th>Tipo</th>
                              <th>Opciones</th>
                              <th>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{question.question_text}</td>
                              <td>{question.question_type.name}</td>
                              <td>
                                <ul>
                                  {question.question_options
                                    .sort(
                                      (a, b) => a.order_index - b.order_index
                                    ) // Ordenar por order_index de menor a mayor
                                    .map(option => (
                                      <li key={option.id}>
                                        {option.option_text} (Orden:{' '}
                                        {option.order_index})
                                      </li>
                                    ))}
                                </ul>
                              </td>
                              <td>
                                <CardDropdown>
                                  <div className="py-2">
                                    <Dropdown.Item
                                      onClick={() => {
                                        setSelectedQuestionId(question.id);
                                        setShowEditQuestionModal(true);
                                      }}
                                    >
                                      Editar
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      className="text-danger"
                                      onClick={() => {
                                        remove(question.id) // Llama a la función remove del servicio
                                          .then(response => {
                                            if (response.result) {
                                              toast.success(
                                                response.msg ||
                                                  'Pregunta eliminada exitosamente.',
                                                { theme: 'colored' }
                                              );
                                              fetchQuestionGroups(); // Recarga los datos de los grupos de preguntas
                                            } else {
                                              toast.error(
                                                'Error al eliminar la pregunta.',
                                                { theme: 'colored' }
                                              );
                                            }
                                          })
                                          .catch(error => {
                                            console.error(
                                              'Error al eliminar la pregunta:',
                                              error
                                            );
                                            toast.error(
                                              'Error al eliminar la pregunta.',
                                              { theme: 'colored' }
                                            );
                                          });
                                      }}
                                    >
                                      Eliminar
                                    </Dropdown.Item>
                                  </div>
                                </CardDropdown>
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      {/* Modal para agregar un grupo */}
      <AddQuestionGroupModal
        survey_id={survey_id}
        show={showAddModal}
        handleClose={() => setShowAddModal(false)}
        refreshGroups={() => setReload(!reload)}
      />

      {/* Modal para editar un grupo */}
      <EditQuestionGroupModal
        group_id={selectedGroupId}
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        refreshGroups={() => setReload(!reload)}
      />

      {/* Modal para agregar una pregunta */}
      <AddQuestionModal
        group_id={selectedGroupId}
        show={showAddQuestionModal}
        handleClose={() => setShowAddQuestionModal(false)}
        refreshQuestions={fetchQuestionGroups}
      />

      {/* Modal para editar una pregunta */}
      <EditQuestionModal
        question_id={selectedQuestionId} // Pasa el ID de la pregunta seleccionada
        show={showEditQuestionModal}
        handleClose={() => setShowEditQuestionModal(false)}
        refreshQuestions={fetchQuestionGroups}
      />
    </Card>
  );
};

EditQuestionsGroups.propTypes = {
  survey_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default EditQuestionsGroups;
